import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, effect } from '@angular/core';
import { FileWithMeta } from '../../models';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { KebabMenuComponent } from 'app/shared/components/kebab-menu/kebab-menu/kebab-menu.component';
import { CreateDamageReportService } from '../../create-damage-report.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Contract } from '@models/contract';
import { filter, switchMap } from 'rxjs';
import { DamageDocumentTypesService } from 'app/customer-portal/damage-reports/damage-document-types.service';
import { splitFileName } from 'app/utils/helper.functions';
import { imgPreview } from 'app/utils/img-preview';

@Component({
    selector: 'app-selected-file',
    imports: [CtrlIconComponent, KebabMenuComponent],
    templateUrl: './selected-file.component.html',
    styleUrl: './selected-file.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectedFileComponent {
    public selectedFile = input.required<FileWithMeta>();

    public onEdit = output<FileWithMeta>();
    public onDelete = output<FileWithMeta>();

    protected menuItems = [
        {
            label: 'Bearbeiten',
            icon: 'pen',
            action: (fwm: FileWithMeta): void => this.onEdit.emit(fwm),
        },
        {
            label: 'Löschen',
            icon: 'trash',
            action: (fwm: FileWithMeta): void => this.onDelete.emit(fwm),
        },
    ];

    protected icon = computed(() => (this.selectedFile().file.type === 'application/pdf' ? 'file_pdf' : 'file'));
    protected docType = computed(() => this.docTypes().find((type) => type.id === this.selectedFile().documentTypeId));
    protected splittedFileName = computed(() => splitFileName(this.selectedFile().file.name));

    private docTypes = toSignal(
        inject(CreateDamageReportService).selectedContract$.pipe(
            takeUntilDestroyed(),
            filter((contract: Contract | null) => !!contract),
            switchMap((contract: Contract) =>
                inject(DamageDocumentTypesService).getDocumentTypes(contract.insuranceType.id),
            ),
        ),
    );

    protected imgSrc = signal<string | ArrayBuffer | null>(null);

    public constructor() {
        // Update imgSrc with a thumbnail preview if the selected file is an image;
        // otherwise, clear imgSrc.
        effect(() => {
            const file = this.selectedFile().file;
            if (file && file.type.startsWith('image/')) {
                imgPreview(file).then((result) => this.imgSrc.set(result));
            } else {
                this.imgSrc.set(null);
            }
        });
    }
}
